import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowRight } from "components/icons/ArrowRight";
import {
  ChangeEvent,
  Fragment,
  ReactNode,
  useContext,
  useId,
  useState,
} from "react";
import { FilterContext } from "./FilterContext";
import { Search } from "../Search/Search";
import { Checkbox } from "../Checkbox/Checkbox";
import InfiniteScroll from "react-infinite-scroll-component";
import { LoadingIcon } from "components/icons/LoadingIcon";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { Funnel } from "components/icons/Funnel";

const List = ({
  children,
  size = "regular",
}: {
  children: ReactNode;
  size?: "regular" | "small";
}) => {
  return (
    <div
      className={classNames(
        "t-w-2/5 t-shrink-0 t-py-5 t-px-3 sm:t-w-32 sm:t-px-2 sm:t-py-3 t-flex t-flex-col t-rounded-l-md t-bg-surface-lighter-grey t-gap-1 t-border-solid t-border-r t-border-l-0 t-border-b-0 t-border-t-0 t-border-purple-10",
        {
          "md:t-min-w-[220px]": size === "regular",
          "md:t-min-w-[180px]": size === "small",
        }
      )}
    >
      {children}
    </div>
  );
};

const Body = ({
  children,
  value,
  block = false,
}: {
  children: ReactNode;
  value: string;
  block?: boolean;
}) => {
  const { value: selectedListItem } = useContext(FilterContext);
  if (selectedListItem === value) {
    return (
      <div
        className={classNames(
          "t-flex-1 t-p-4 sm:t-p-3 md:t-p-6 t-rounded-r-md t-overflow-auto t-min-w-0",
          {
            "t-w-full": block,
          }
        )}
      >
        {children}
      </div>
    );
  }
  return null;
};

const Portal = ({
  children,
  size = "regular",
  ...props
}: {
  children: ReactNode;
  size?: "small" | "regular";
}) => {
  return (
    <Dropdown.Content
      onFocusOutside={(e) => e.preventDefault()}
      sideOffset={8}
      align="start"
      side="bottom"
      collisionPadding={12}
      className={classNames(
        "t-flex t-rounded-md t-shadow-filter-shadow t-border t-border-solid !t-border-purple-10 t-max-w-[calc(100vw-24px)] t-max-h-[calc(100dvh-120px)]",
        {
          "t-h-[360px] t-w-[450px]": size === "small",
          "t-h-[440px] t-w-[600px] ": size === "regular",
        }
      )}
      {...props}
    >
      {children}
    </Dropdown.Content>
  );
};

const Root = ({
  children,
  defaultValue,
  title = "Filters",
  capsule,
  trigger,
  onOpenChange: onOpenChangeFromProps,
  open: openFromProps,
  hideDivider,
  withWrapper = true,
  wrapperClassname = "",
}: {
  children: ReactNode;
  defaultValue: string;
  title?: ReactNode;
  capsule?:
    | ReactNode
    | (({
        openFilterSection,
      }: {
        openFilterSection: (value: string) => void;
      }) => ReactNode);
  trigger?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  hideDivider?: boolean;
  withWrapper?: boolean;
  wrapperClassname?: string;
}) => {
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(openFromProps || false);

  const onSelect = (value: string) => {
    if (value) {
      setValue(value);
    }
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (onOpenChangeFromProps) {
      onOpenChangeFromProps(open);
    }
  };

  const openFilterSection = (value: string) => {
    setValue(value);
    setOpen(true);
  };

  const Wrapper = withWrapper ? "div" : Fragment;
  const wrapperProps = withWrapper
    ? /* @tw */
      {
        className:
          "t-w-fit t-flex t-gap-2 t-h-full t-flex-wrap t-items-center " +
          wrapperClassname,
      }
    : {};

  return (
    <FilterContext.Provider
      value={{ value, onSelect, setOpen, open: openFromProps || open }}
    >
      <Dropdown.Root
        open={openFromProps || open}
        onOpenChange={onOpenChange}
        modal={false}
      >
        <Wrapper {...wrapperProps}>
          <Dropdown.Trigger asChild>
            {trigger ? (
              trigger
            ) : (
              <div className="t-w-fit">
                <Button size="xsmall" type="button">
                  <div className="t-flex t-gap-1.5 t-select-none t-items-center">
                    <Funnel />
                    {title && <div className="t-text-text-60">{title}</div>}
                  </div>
                </Button>
              </div>
            )}
          </Dropdown.Trigger>
          {capsule && !hideDivider && (
            <div className="t-border-0 t-border-solid t-border-neutral-10 t-h-6 t-border-l" />
          )}
          {typeof capsule === "function"
            ? capsule({ openFilterSection })
            : capsule}
        </Wrapper>
        <Dropdown.Portal>{children}</Dropdown.Portal>
      </Dropdown.Root>
    </FilterContext.Provider>
  );
};

const ListItem = ({
  children,
  value,
}: {
  children: ReactNode;
  value: string;
}) => {
  const { value: selectedListItem, onSelect } = useContext(FilterContext);
  const isSelected = Boolean(selectedListItem === value);

  return (
    <Dropdown.Item
      onSelect={(e) => {
        e.preventDefault();
        onSelect(value);
      }}
      textValue=""
      className={classNames(
        "all:unset !t-px-4 t-py-2 t-h-[34px] sm:t-h-auto sm:t-min-h-[34px] sm:!t-whitespace-normal sm:t-overflow-visible t-items-center t-rounded !t-text-body-sm t-flex t-justify-between hover:!t-bg-purple-10 t-transform t-transition-colors t-duration-500 t-ease-in-out",
        {
          "!t-bg-purple-10 t-text-text-100": isSelected,
          "t-text-text-60": !isSelected,
        }
      )}
    >
      <div className="sm:t-min-w-0 sm:t-break-words">{children}</div>

      <div
        className={classNames(
          "t-transform -t-translate-x-4 t-transition-all t-duration-300 t-ease-in-out",
          { "t-translate-x-0": isSelected }
        )}
      >
        {isSelected && <ArrowRight color="black" stroke="1.4" />}
      </div>
    </Dropdown.Item>
  );
};

type Option = { label: string; value: string; disable?: boolean };

export const MultiSelectFilter = ({
  options = [],
  selected: selectedFromProps = [],
  onChange,
  totalCount,
  loadNext,
  hasMore,
  onSearchValueChange,
  isSearchable = true,
  isSelectAll = true,
  checkboxSpace = "regular",
  className,
}: {
  options: Option[];
  selected?: string[];
  onChange: (newValues: string[]) => void;
  hasMore?: boolean;
  loadNext?: () => void;
  totalCount?: number;
  onSearchValueChange?: (searchValue: string) => void;
  isSearchable?: boolean;
  isSelectAll?: boolean;
  checkboxSpace?: "regular" | "small";
  className?: string;
}) => {
  const [searchValue, setSearch] = useState("");
  const infiniteScrollId = useId();
  const selected = selectedFromProps.filter(Boolean) || [];
  const addOrRemoveOption = (e: ChangeEvent<HTMLInputElement>) => {
    const currentOption = options.find(({ value }) => value === e.target.name)!;

    if (e.target.checked) {
      const newSelected = new Set([...selected, currentOption.value]);
      return onChange(Array.from(newSelected));
    }

    const selectedOptionsExcludingCurrent = selected.filter(
      (v) => v !== currentOption?.value
    );

    onChange(selectedOptionsExcludingCurrent);
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    if (onSearchValueChange) {
      return onSearchValueChange(e.target.value);
    }
    setSearch(e.target.value);
  };

  const filterOptions = options.filter((o) =>
    searchValue
      ? o.label?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
      : true
  );

  const selectAll = () => {
    if (selected.length === filterOptions.length) {
      return onChange([]);
    }

    const newSelected = filterOptions.map((o) => o.value);
    onChange(newSelected);
  };

  return (
    <DashboardContainer
      className={classNames("t-h-full t-gap-4", {
        [className || ""]: Boolean(className),
      })}
    >
      {isSearchable && (
        <DashboardContainer.Header>
          <Search
            onChange={onSearch}
            placeholder="Search"
            className="t-w-full"
          />
        </DashboardContainer.Header>
      )}
      <DashboardContainer.Content>
        <div className="t-overflow-y-auto" id={infiniteScrollId}>
          {!hasMore && isSelectAll && (
            <div
              className={classNames("t-flex t-justify-between", {
                "t-mb-5": checkboxSpace === "regular",
                "t-mb-2": checkboxSpace === "small",
              })}
            >
              <Checkbox
                onChange={selectAll}
                label="Select All"
                checked={options.every((o) => selected.includes(o.value))}
                name="select-all"
              />

              <Button
                customType="ghost"
                size="small"
                onClick={() => onChange([])}
              >
                <span className="t-text-text-30">Clear all</span>
              </Button>
            </div>
          )}

          {filterOptions.length === 0 ? (
            <div className="t-flex t-justify-center t-items-center t-h-40 t-text-text-60">
              No results found
            </div>
          ) : (
            <InfiniteScroll
              dataLength={totalCount || 0}
              next={loadNext || function () {}}
              hasMore={hasMore || false}
              scrollableTarget={infiniteScrollId}
              className={classNames({
                "t-space-y-5": checkboxSpace === "regular",
                "t-space-y-3": checkboxSpace === "small",
              })}
              loader={
                <div className="t-flex t-justify-center t-items-center t-w-full t-gap-2 t-p-2">
                  <span className="t-flex t-origin-center t-animate-spin">
                    <LoadingIcon />
                  </span>
                  Loading...
                </div>
              }
            >
              {filterOptions.map(({ value, label, disable }) => (
                <Checkbox
                  key={value}
                  onChange={addOrRemoveOption}
                  name={value}
                  label={label}
                  checked={selected.includes(value)}
                  disabled={disable}
                />
              ))}
            </InfiniteScroll>
          )}
        </div>
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};

/**
 * Anatomy of Filter component
 * @example
 * ```tsx
 * <Filter.Root defaultValue="item1" title="Filters">
 *   <Filter.Portal>
 *     <Filter.List>
 *       <Filter.ListItem value="item1">Item 1</Filter.ListItem>
 *       <Filter.ListItem value="item2">Item 2</Filter.ListItem>
 *     </Filter.List>
 *     <Filter.Body value="item1">
 *       <div>Content for Item 1</div>
 *     </Filter.Body>
 *     <Filter.Body value="item2">
 *       <div>Content for Item 2</div>
 *     </Filter.Body>
 *   </Filter.Portal>
 * </Filter.Root>
 * ```
 */
export const Filter = {
  Root: Root,
  Portal: Portal,
  List: List,
  ListItem: ListItem,
  Body: Body,
};
